import { Commit, Dispatch, Module } from 'vuex';
import { Toast } from '@/model/Toast';
import Vue from 'vue';

interface ToastState {
  toasts: Toast[];
  nextId: number;
}

const activeTimeouts: Map<number, number> = new Map();

function setToastTimeout(dispatch: Dispatch, commit: Commit, toast: Toast): void {
  if (toast.timeout && toast.timeout > 0) {
    const timeoutId = window.setTimeout(() => {
      dispatch('remove', toast.id);
    }, toast.timeout);
    commit('setTimeout', { toastId: toast.id, timeoutId });
  }
}

function findDuplicateToast(toasts: Toast[], newToast: Partial<Toast>): Toast | undefined {
  if (newToast.allowDuplicates === true) {
    return undefined;
  }
  
  return toasts.find((t) => 
    t.allowDuplicates !== true &&
    t.type === newToast.type &&
    t.title === newToast.title &&
    t.content === newToast.content
  );
}

function createToast(state: ToastState, toast: Partial<Toast>) {
  const timeout = toast.timeout !== undefined
    ? toast.timeout
    : toast.dismissible === undefined
    ? 5000
    : undefined;
    
  return {
    ...toast,
    type: toast.type ?? 'info',
    title: toast.title ?? 'Notification',
    id: toast.id ?? state.nextId,
    dismissible:
      toast.dismissible !== undefined
        ? toast.dismissible
        : toast.timeout === 0 || toast.timeout === undefined,
    timeout,
    timeoutSetAt: timeout && timeout > 0 ? Date.now() : undefined,
  } satisfies Toast;
}

const toastModule: Module<ToastState, any> = {
  namespaced: true,
  state: {
    toasts: <Toast[]>[],
    nextId: 1,
  },
  mutations: {
    addToast(state, toast: Toast) {
      state.toasts.push(toast);
      state.nextId++;
    },
    removeToast(state, toastId: number) {
      state.toasts = state.toasts.filter((toast) => toast.id !== toastId);
    },
    replaceToast(state, toast: Toast) {
      const index = state.toasts.findIndex((t) => t.id === toast.id);
      if (index !== -1) {
        state.toasts.splice(index, 1, toast);
      } else {
        state.toasts.push(toast);
      }
    },
    clearTimeout(_state, toastId: number) {
      if (activeTimeouts.has(toastId)) {
        clearTimeout(activeTimeouts.get(toastId)!);
        activeTimeouts.delete(toastId);
      }
    },
    setTimeout(_state, { toastId, timeoutId }: { toastId: number; timeoutId: number }) {
      if (activeTimeouts.has(toastId)) {
        clearTimeout(activeTimeouts.get(toastId)!);
      }
      activeTimeouts.set(toastId, timeoutId);
    },
  },
  actions: {
    add({ commit, state, dispatch }, toast: Omit<Toast, 'id'>): Promise<number> {
      return new Promise((resolve) => {
        const existingToast = findDuplicateToast(state.toasts, toast);
        
        if (existingToast) {
          const mergedToast = createToast(state, {
            ...existingToast,
            ...toast,
            id: existingToast.id,
          });
          
          commit('clearTimeout', existingToast.id);
          commit('replaceToast', mergedToast);
          setToastTimeout(dispatch, commit, mergedToast);
          
          Vue.nextTick(() => {
            resolve(existingToast.id);
          });
        } else {
          const newToast = createToast(state, toast);
      
          commit('addToast', newToast);
      
          setToastTimeout(dispatch, commit, newToast);
      
          Vue.nextTick(() => {
            resolve(newToast.id);
          });
        }
      });
    },
    remove({ commit, state }, toastId: number): Promise<void> {
      return new Promise((resolve) => {
        const toastExists = state.toasts.some((toast) => toast.id === toastId);
  
        if (toastExists) {
          commit('clearTimeout', toastId);
          commit('removeToast', toastId);
  
          Vue.nextTick(() => {
            resolve();
          });
        } else {
          resolve();
        }
      });
    },
    replace({ commit, state, dispatch }, toast: Toast): Promise<void> {
      return new Promise((resolve) => {
        const newToast = createToast(state, toast);

        const existingToasts = state.toasts.filter((t) => t.id !== newToast.id);
        const duplicateToast = findDuplicateToast(existingToasts, newToast);
        
        if (duplicateToast) {
          commit('clearTimeout', newToast.id);
          commit('removeToast', newToast.id);
          
          const updatedToast = createToast(state, {
            ...duplicateToast,
            ...newToast,
            id: duplicateToast.id,
          });
          
          commit('clearTimeout', duplicateToast.id);
          commit('replaceToast', updatedToast);
          setToastTimeout(dispatch, commit, updatedToast);
          
          Vue.nextTick(() => {
            resolve();
          });
        } else {
          const toastExists = state.toasts.some((t) => t.id === newToast.id);
    
          if (toastExists) {
            commit('clearTimeout', newToast.id);
            commit('replaceToast', newToast);

            setToastTimeout(dispatch, commit, newToast);
    
            Vue.nextTick(() => {
              resolve();
            });
          } else {
            resolve();
          }
        }
      });
    }
  }
};

export default toastModule;